import { findKey } from 'lodash';

import type { Logger } from '../../common/types/Logger';
import { type User, UserType } from '../../common/types/Membership';
import { transformEnumToDisplay } from '../../common/util/utils';
import {
    IncomePayCycle,
    IncomeType,
    type MembershipIncome,
} from '../types/Income';

/* Constant conservatively estimating number of weeks in a month */
const EST_WEEKS_IN_MONTH = 4;

const getIncomeOwnerFallbackLabel = (userIndex: number): string =>
    userIndex === 0 ? 'Mine' : 'My partner';

const getIncomeOwnerBaseLabel = (user: User, userIndex: number): string => {
    const firstName = user.firstName?.trim();

    return firstName != null && firstName !== ''
        ? firstName
        : getIncomeOwnerFallbackLabel(userIndex);
};

const getIncomeOwnerRoleLabel = (user: User, userIndex: number): string => {
    if (user.type === UserType.PRIMARY_MEMBER) {
        return 'primary';
    }

    if (user.type === UserType.PARTNER_MEMBER) {
        return 'partner';
    }

    return `member ${userIndex + 1}`;
};

// Map of users, who are income owners, where the key is the user's display label and the value is the userId.
// This map is helpful in handling display vs data values in forms, since we want the userId to be the data value, but for the display value to be the user's name.
export const getIncomeOwnerOptionsMap = (
    users: User[],
): Record<string, string> => {
    const baseLabels = users.map(getIncomeOwnerBaseLabel);

    return users.reduce<Record<string, string>>((acc, currValue, currIndex) => {
        const baseLabel = baseLabels[currIndex];

        if (baseLabel == null) {
            return acc;
        }

        const isDuplicateLabel =
            baseLabels.filter((label) => label === baseLabel).length > 1;
        const displayName = isDuplicateLabel
            ? `${baseLabel} (${getIncomeOwnerRoleLabel(currValue, currIndex)})`
            : baseLabel;

        return {
            ...acc,
            [displayName]: currValue.id,
        };
    }, {});
};

export const getDisplayNameFromUserId = (
    userId: string,
    options: Record<string, string>,
    logger?: Logger,
) => {
    const displayName = findKey(options, (value) => value === userId);
    if (displayName == null) {
        logger?.captureException(
            new Error('Could not determine display name for income owner.'),
        );
    }

    return displayName ?? '';
};

// Returns a function that can be invoked to return a display value, given the data value (a user's id).
export const getIncomeOwnerDisplayFormatter = (
    users: User[],
    logger?: Logger,
): ((userId?: string) => string) => {
    const options = getIncomeOwnerOptionsMap(users);

    return (userId?: string): string => {
        if (userId == null || userId === '') {
            return '';
        }

        return getDisplayNameFromUserId(userId, options, logger);
    };
};

// Formats income type enum to be used as display values.
export const formatIncomeType = (incomeType: string) => {
    const incomeTypeEnum = incomeType as IncomeType;

    if (incomeTypeEnum === IncomeType.FULL_TIME_SALARY) {
        return 'Full-time salary';
    } else if (incomeTypeEnum === IncomeType.PART_TIME_WAGE) {
        return 'Part-time';
    }

    return transformEnumToDisplay(incomeType);
};

// Formats pay cycle enum to be used as display values.
export const formatPayCycle = (payCycle: string) => {
    const payCycleEnum = payCycle as IncomePayCycle;

    if (payCycleEnum === IncomePayCycle.BI_WEEKLY) {
        return 'Bi-weekly';
    } else if (payCycleEnum === IncomePayCycle.SEMI_MONTHLY) {
        return 'Semi-monthly';
    }

    return transformEnumToDisplay(payCycle);
};

// Gets the detailed text for the pay cycle enum when used as a display.
export const getPayCycleDisplayDetails = (payCycle: string) => {
    const payCycleEnum = payCycle as IncomePayCycle;

    if (payCycleEnum === IncomePayCycle.BI_WEEKLY) {
        return 'Every 2 weeks';
    } else if (payCycleEnum === IncomePayCycle.SEMI_MONTHLY) {
        return 'Twice each month';
    }

    return null;
};

/**
 * Conservatively calculate the monthly take home cents from a given set of incomes
 */
export const calculateMonthlyTakeHomeCents = (incomes: MembershipIncome[]) =>
    incomes.reduce((sum, income) => sum + getMonthlyCentsFromIncome(income), 0);

/**
 * Conservatively calculate the monthly take home cents from a given income
 *
 * 2025-09-18: For weekly income, guides requested we use EST_WEEKS_IN_MONTH rather than
 *             a full year estimate (income * 52 / 12) to give their members a more conservative
 *             estimate.
 */
export const getMonthlyCentsFromIncome = (income: MembershipIncome) =>
    income.monthlyTakeHomeCents ??
    (income.weeklyTakeHomeCents ?? 0) * EST_WEEKS_IN_MONTH;

const roundToWholeDollarCents = (cents: number): number =>
    Math.round(cents / 100) * 100;

/**
 * Converts a per-pay-period take-home amount to an estimated monthly total for Money Mapper.
 * Weekly: 4× per-week amount (same factor as {@link getMonthlyCentsFromIncome}).
 * Bi-weekly and semi-monthly: 2× per paycheck (guides assume the minimum paychecks in a typical
 * month — two — not a 26/12 annual average).
 */
export const moneyMapperPayPeriodToMonthlyCents = (
    payCycle: IncomePayCycle,
    periodAmountCents: number,
): number => {
    switch (payCycle) {
        case IncomePayCycle.WEEKLY:
            return roundToWholeDollarCents(
                periodAmountCents * EST_WEEKS_IN_MONTH,
            );
        case IncomePayCycle.BI_WEEKLY:
        case IncomePayCycle.SEMI_MONTHLY:
            return roundToWholeDollarCents(periodAmountCents * 2);
        case IncomePayCycle.MONTHLY:
            return roundToWholeDollarCents(periodAmountCents);
        case IncomePayCycle.OTHER:
        default:
            return roundToWholeDollarCents(periodAmountCents);
    }
};

/**
 * Derives pay period amount and pay cycle for editing in Money Mapper from stored membership income.
 */
export const getMoneyMapperPeriodCentsAndPayCycle = (
    income: MembershipIncome,
): { periodCents: number; payCycle: IncomePayCycle } => {
    const payCycle = income.payCycle ?? IncomePayCycle.MONTHLY;
    const monthly = income.monthlyTakeHomeCents ?? 0;
    const weekly = income.weeklyTakeHomeCents ?? 0;

    /**
     * Weekly pay is stored with both weekly + derived monthly (API). Prefer showing weekly
     * amount + WEEKLY whenever `payCycle` says so — do not fall through to the monthly branch
     * (which would incorrectly show MONTHLY).
     */
    if (income.payCycle === IncomePayCycle.WEEKLY && weekly > 0) {
        return {
            periodCents: weekly,
            payCycle: IncomePayCycle.WEEKLY,
        };
    }

    if (weekly > 0 && income.monthlyTakeHomeCents == null) {
        return {
            periodCents: weekly,
            payCycle: IncomePayCycle.WEEKLY,
        };
    }

    if (monthly > 0) {
        if (
            payCycle === IncomePayCycle.BI_WEEKLY ||
            payCycle === IncomePayCycle.SEMI_MONTHLY
        ) {
            return {
                periodCents: Math.round(monthly / 2),
                payCycle,
            };
        }
        return {
            periodCents: monthly,
            payCycle:
                payCycle === IncomePayCycle.WEEKLY && weekly <= 0
                    ? IncomePayCycle.MONTHLY
                    : payCycle,
        };
    }

    return {
        periodCents: 0,
        payCycle: IncomePayCycle.MONTHLY,
    };
};

export const moneyMapperStoredTakeHomeFromPeriod = (
    payCycle: IncomePayCycle,
    periodAmountCents: number,
): {
    monthlyTakeHomeCents: number | null;
    weeklyTakeHomeCents: number | null;
} => {
    if (payCycle === IncomePayCycle.WEEKLY) {
        return {
            weeklyTakeHomeCents: periodAmountCents,
            /** Same factor as {@link getMonthlyCentsFromIncome} — API expects monthly when saving. */
            monthlyTakeHomeCents: moneyMapperPayPeriodToMonthlyCents(
                IncomePayCycle.WEEKLY,
                periodAmountCents,
            ),
        };
    }

    return {
        weeklyTakeHomeCents: null,
        monthlyTakeHomeCents: moneyMapperPayPeriodToMonthlyCents(
            payCycle,
            periodAmountCents,
        ),
    };
};
